// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : error renders
// =============================================================================

// ---Third-party imports
import React, {Component} from 'react'
import {Table, Button, Header, Icon, Message} from 'semantic-ui-react'

// ---Framework imports
import {uix, Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {mapItems, rstrip, isString} from 'ldash'
import connect from 'uix/connect'
import {useSelector, useEffect, useState, useMemo, useActions} from 'uix/hooks'

import './error.scss'

// ---Exports
// <Message.List items={(error.traceback || []).map(e => JSON.stringify(e))} />
const silver = s => <span style={{color: 'silver'}}>{s}</span>
const red = s => <span style={{color: 'red', fontWeight: 'bold'}}>{s}</span>

export const ApiErrorMessage = ({error, traceback = false}) => {
  /* UI-Element that formats error messages returned by useApi
    TODO: maybe use <TracebackDiv> here somehow
  */
  return (
    <Message negative size="mini">
      <Message.Header>
        {red(error.error)}: <tt>{error.errorMsg}</tt>
      </Message.Header>
      {traceback && TracebackDiv(error.traceback, '', '', {})}
    </Message>
  )
}

export const TracebackDiv = (traceback, error, errorMsg, errorInfo) => {
  let divs = []
  if (!traceback) {
    divs.push(
      <div key="no_tr" style={{paddingLeft: '20px'}}>
        NO TRACEBACK
      </div>
    )
  } else {
    for (let [i, e] of traceback.entries()) {
      divs.push(
        <div key={i * 2} style={{paddingLeft: '20px'}}>
          {`File ${e[0]}, Line `}
          {silver(e[1])}
          {`, in `}
          {silver(e[2])}
        </div>
      )
      divs.push(<div key={i * 2 + 1} style={{paddingLeft: '40px'}}>{`${e[3]}`}</div>)
    }
  }
  let moreInfo = mapItems(errorInfo, (k, v) => (
    <div key={k}>
      {red(k)}
      {`: `}
      {silver(isString(v) ? v : JSON.stringify(v))}
    </div>
  ))
  return (
    <code>
      <div>Traceback (most recent call last):</div>
      {divs}
      {(error || errorMsg) && (
        <div>
          {red(error)}
          {`: `}
          {silver(errorMsg)}
        </div>
      )}
      {moreInfo}
    </code>
  )
}

const ErrorFallbackComponent = ({componentStack, error, customProps, relatedErrorId}) => {
  let stack = null
  let deploy = useSelector(root => root.config.deploy)
  let apiurl = useSelector(Sel.apiurl)
  let request = useSelector(Sel.request)
  let reportErrorBoundary = useActions('reportErrorBoundary')

  let [stacktracey, setStackTracey] = useState(null)
  customProps = customProps || {}
  useEffect(() => {
    import(/* webpackChunkName: 'stacktracey' */ 'stacktracey').then(module => {
      setStackTracey({StackTracey: module.default})
    })
  }, [])
  let enableTracy = deploy.mode != 'npm'
  let formattedStack = useMemo(() => {
    if (stacktracey && enableTracy) {
      let _stack = new stacktracey.StackTracey(error)
      return _stack
        .withSources()
        .clean()
        .asTable()
    }
  }, [stacktracey, error])

  let errorMessage = error.toString().split('\n')[0]

  useEffect(() => {
    // send only if non npm-dev-server
    if (enableTracy && formattedStack) {
      reportErrorBoundary({
        otid: relatedErrorId,
        errorMessage,
        errorStack: formattedStack,
        componentStack,
      })
    }
  }, [formattedStack])

  let pre_style = {fontSize: '0.7em', margin: 0, lineHeight: '1.2em'}

  let content = <i style={{color: 'red'}}>See dev console for details</i>
  if (enableTracy) {
    // in npm/dev we get
    // a) wrong line numbers
    // b) CORS errors du to webpack-internal:// protocol usage
    content = <pre style={pre_style}>{formattedStack}</pre>
  }
  stack = (
    <div>
      <strong>
        <tt>Stack: </tt>
      </strong>
      {content}
    </div>
  )

  let location_href = apiurl.baseUrl(request.pathname)
  return (
    <div
      style={{height: '100%', overflow: 'auto', fontSize: 'small', textAlign: 'left'}}
      {...customProps}
    >
      <strong>
        <tt>{errorMessage}</tt>
      </strong>
      <div>
        on <a href={location_href}>{location_href}</a>
        {relatedErrorId && ` - [${relatedErrorId}]`}
      </div>
      {stack}
      <br />
      <span>
        In
        <i>
          <tt> {customProps.name}</tt>
        </i>
      </span>
      <pre style={pre_style}>{componentStack}</pre>
    </div>
  )
}

const errorHandler = (error, componentStack) => {
  log.warn('error during render')
  // XXX: send to server
  // log.error(error)
  // log.error(componentStack)
}

// you can add "// @refresh reset" anywhere in the file you're editing.
// This directive is local to the file, and instructs Fast Refresh to
// remount components defined in that file on every edit.
// Dirk: XXX - explain why this is a good idea here (currently it's only a guess)
/* @refresh reset */
class _ErrorBoundary extends Component {
  // 1) we reset after runId update (goto next view)
  // 2) we reset only on first HMR event (webpack)
  // after the first HMR event - fast-refresh starts to handle render errors
  // XXX: this does work for components that are remounted after hot-reload
  // Dirk: does the above comment still holds after switch from react-hot-load to fast-refresh
  static storeMap = root => ({
    hmrgen: Sel.hmrGen(root) > 0 ? 1 : Sel.hmrGen(root),
    runId: Sel.runId(root),
  })

  static getDerivedStateFromProps(props, state) {
    if (!state || props.hmrgen > state.hmrgen || props.runId > state.runId) {
      //~ log.warn('reset ErrorBoundary')
      return {
        error: null,
        info: null,
        hmrgen: props.hmrgen,
        runId: props.runId,
      }
    }
    return null
  }

  constructor(props) {
    super(props)
    this.state = _ErrorBoundary.getDerivedStateFromProps(props)
  }

  componentDidCatch(error, info) {
    try {
      /* istanbul ignore next: Ignoring ternary; can’t reproduce missing info in test environment. */
      errorHandler.call(this, error, info ? info.componentStack : '')
    } catch (ignoredError) {}

    this.setState({error, info})
  }

  render() {
    const {children, showFallback = true, name, fallbackProps = {}, relatedErrorId} = this.props

    const {error, info} = this.state
    if (error !== null) {
      log.error('render ErrorBoundary: %o', name)
      if (!showFallback) {
        return null
      }
      return (
        <ErrorFallbackComponent
          componentStack={info ? info.componentStack : ''}
          error={error}
          customProps={{...fallbackProps, name}}
          relatedErrorId={relatedErrorId}
        />
      )
    }

    return children || null
  }
}

export const ErrorBoundary = connect(
  'ConnectedErrorBoundary',
  _ErrorBoundary
)
